import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { useGetWorkflow, useUpdateWorkflow, type PlainWorkflow } from "@/queries/automation-queries";
import { workflowDetailsUrl, workflowName, workflowsUrl } from "@/utils/automations";
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";
import { AutomationDefinitionSectionEdit } from "@/routes/automations/automation-edit-page/AutomationDefinitionSectionEdit";
import { SaveAutomationStepsButton } from "@/routes/automations/automation-edit-page/SaveAutomationStepsButton";
import { atom, useAtom } from "jotai";
import { atomEffect } from "jotai-effect";
import {
    convertWorkflowActionToFormSteps,
    mergeFormDataIntoWorkflow,
    type WorkflowEditFormData,
    type WorkflowEditFormStepData,
    type WorkflowEditFormStepType,
    type WorkflowEditFormReportOutputData,
} from "@/components/automations/automation-edit-form-data";
import { newWorkflowFromTemplate, type AutomationTemplate } from "@/utils/automation-templates";
import CreateAutomationButton from "@/routes/automations/automation-edit-page/CreateAutomationButton";
import { AutomationEditableHeader } from "@/routes/automations/automation-edit-page/AutomationEditableHeader";
import AutomationEditDialog from "@/components/automations/AutomationEditDialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { v4 as uuidv4 } from "uuid";
import type { DraftStep } from "@/routes/automations/automation-edit-page/types";
import { useDocumentTitle } from "@/hooks/use-document-title";

/**
 * Creates all atoms needed for the edit page.
 * These atoms are scoped to this component instance.
 */
function useEditPageAtoms() {
    return useMemo(() => {
        // Core state atoms
        const editableStepsAtom = atom<WorkflowEditFormStepData[] | null>(null);
        const stepIndexBeingEditedAtom = atom<number | null>(null);
        const newStepAtom = atom<WorkflowEditFormStepData | null>(null);
        const draftStepAtom = atom<DraftStep | null>(null);

        // Derived atom: is a new step being created
        const isCreatingStepAtom = atom(
            (get) => get(stepIndexBeingEditedAtom) === null && get(newStepAtom) !== null,
        );

        // Action atom: replace step at index
        const replaceStepAtom = atom(
            null,
            (get, set, { index, step }: { index: number; step: WorkflowEditFormStepData }) => {
                const steps = get(editableStepsAtom);
                if (steps) {
                    set(editableStepsAtom, [...steps.slice(0, index), step, ...steps.slice(index + 1)]);
                }
            },
        );

        // Action atom: remove step at index
        const removeStepAtom = atom(null, (get, set, index: number) => {
            const steps = get(editableStepsAtom);
            if (steps) {
                set(
                    editableStepsAtom,
                    steps.filter((_, i) => i !== index),
                );
                const selectedIndex = get(stepIndexBeingEditedAtom);
                if (selectedIndex !== null) {
                    if (index < selectedIndex) {
                        set(stepIndexBeingEditedAtom, selectedIndex - 1);
                    } else if (index === selectedIndex) {
                        set(stepIndexBeingEditedAtom, null);
                    }
                }
            }
        });

        // Action atom: create new step
        const createNewStepAtom = atom(null, (_get, set, type: WorkflowEditFormStepType = "command") => {
            set(stepIndexBeingEditedAtom, null);
            switch (type) {
                case "prompt":
                case "command":
                    set(newStepAtom, {
                        id: uuidv4(),
                        type,
                        content: "",
                    });
                    break;
                case "pr":
                    set(newStepAtom, {
                        id: uuidv4(),
                        type,
                        title: "",
                        description: "",
                        branch: "",
                        isDraft: false,
                    });
                    break;
                case "report":
                    set(newStepAtom, {
                        id: uuidv4(),
                        type,
                        outputs: [],
                    });
                    break;
            }
        });

        // Effect: create step if empty
        const createStepIfEmptyAtomEffect = atomEffect((get, set) => {
            const steps = get(editableStepsAtom);
            if (steps && steps.length === 0) {
                set(createNewStepAtom, "command");
            }
        });

        // Effect: create draft from selected step or new step
        const createDraftAtomEffect = atomEffect((get, set) => {
            const index = get(stepIndexBeingEditedAtom);
            const newStep = get(newStepAtom);
            const step = index !== null ? get(editableStepsAtom)?.[index] : null;
            const sourceStep = step ?? newStep;
            if (sourceStep) {
                switch (sourceStep.type) {
                    case "prompt":
                    case "command":
                        set(draftStepAtom, {
                            id: sourceStep.id,
                            type: sourceStep.type,
                            content: atom(sourceStep.content),
                        });
                        break;
                    case "pr":
                        set(draftStepAtom, {
                            id: sourceStep.id,
                            type: "pr",
                            title: atom(sourceStep.title),
                            description: atom(sourceStep.description),
                            branch: atom(sourceStep.branch),
                            isDraft: atom(sourceStep.isDraft),
                        });
                        break;
                    case "report":
                        set(draftStepAtom, {
                            id: sourceStep.id,
                            type: "report",
                            outputs: atom(sourceStep.outputs),
                        });
                        break;
                }
            } else {
                set(draftStepAtom, null);
            }
        });

        // Action atom: change draft step type
        const changeDraftStepTypeAtom = atom(null, (get, set, type: WorkflowEditFormStepType) => {
            const draftStep = get(draftStepAtom);
            if (!draftStep) {
                return;
            }

            const currentType = draftStep.type;
            const canPreserveContent =
                (currentType === "prompt" || currentType === "command") &&
                (type === "prompt" || type === "command");

            if (canPreserveContent) {
                set(draftStepAtom, {
                    ...draftStep,
                    type,
                });
            } else {
                switch (type) {
                    case "command":
                    case "prompt":
                        set(draftStepAtom, {
                            id: draftStep.id,
                            type,
                            content: atom(""),
                        });
                        break;
                    case "pr":
                        set(draftStepAtom, {
                            id: draftStep.id,
                            type,
                            title: atom(""),
                            description: atom(""),
                            branch: atom(""),
                            isDraft: atom(false),
                        });
                        break;
                    case "report":
                        set(draftStepAtom, {
                            id: draftStep.id,
                            type,
                            outputs: atom<WorkflowEditFormReportOutputData[]>([]),
                        });
                        break;
                }
            }
        });

        // Action atom: upsert draft step into steps
        const upsertDraftStepIntoStepsAtom = atom(null, (get, set) => {
            const draftStep = get(draftStepAtom);
            if (!draftStep) {
                return;
            }

            let newStep: WorkflowEditFormStepData;
            switch (draftStep.type) {
                case "command":
                case "prompt":
                    newStep = {
                        id: draftStep.id,
                        type: draftStep.type,
                        content: get(draftStep.content),
                    };
                    break;
                case "pr":
                    newStep = {
                        id: draftStep.id,
                        type: draftStep.type,
                        title: get(draftStep.title),
                        description: get(draftStep.description),
                        branch: get(draftStep.branch),
                        isDraft: get(draftStep.isDraft),
                    };
                    break;
                case "report":
                    newStep = {
                        id: draftStep.id,
                        type: draftStep.type,
                        outputs: get(draftStep.outputs),
                    };
                    break;
            }

            const editIndex = get(stepIndexBeingEditedAtom);

            if (editIndex !== null) {
                set(replaceStepAtom, { index: editIndex, step: newStep });
                set(stepIndexBeingEditedAtom, null);
                return false;
            } else {
                const steps = get(editableStepsAtom);
                set(editableStepsAtom, steps ? [...steps, newStep] : [newStep]);
                set(createNewStepAtom, "command");
                return true;
            }
        });

        return {
            editableStepsAtom,
            stepIndexBeingEditedAtom,
            newStepAtom,
            draftStepAtom,
            isCreatingStepAtom,
            removeStepAtom,
            createNewStepAtom,
            createStepIfEmptyAtomEffect,
            createDraftAtomEffect,
            changeDraftStepTypeAtom,
            upsertDraftStepIntoStepsAtom,
        };
    }, []);
}

type AutomationEditPageProps = {
    template?: AutomationTemplate;
};

export const AutomationEditPage: FC<AutomationEditPageProps> = ({ template }) => {
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [workflow, setWorkflow] = useState<PlainWorkflow | undefined>(undefined);
    const navigate = useNavigate();
    const { toast } = useToast();

    const { workflowId } = useParams<{ workflowId: string }>();
    const { data: workflowData, error: workflowError } = useGetWorkflow(template ? undefined : workflowId);
    const { mutateAsync: updateWorkflow } = useUpdateWorkflow();

    // Set document title based on whether we're editing an existing workflow or creating a new one
    const documentTitle = template ? undefined : workflow ? `Edit ${workflowName(workflow)}` : undefined;
    useDocumentTitle(documentTitle);

    const atoms = useEditPageAtoms();
    const [editableSteps, setEditableSteps] = useAtom(atoms.editableStepsAtom);
    const hasInitializedSteps = useRef(false);

    useEffect(() => {
        if (template) {
            setWorkflow(newWorkflowFromTemplate(template));
        } else if (workflowId) {
            setWorkflow(workflowData);
        }
    }, [workflowData, template, workflowId]);

    useEffect(() => {
        if (hasInitializedSteps.current) {
            return;
        }
        if (workflow?.spec?.action) {
            setEditableSteps(convertWorkflowActionToFormSteps(workflow.spec.action));
            hasInitializedSteps.current = true;
        } else if (workflow) {
            setEditableSteps([]);
            hasInitializedSteps.current = true;
        }
    }, [workflow, setEditableSteps]);

    const handleCancel = useCallback(() => {
        if (template) {
            navigate(workflowsUrl());
        }
        if (workflowId) {
            navigate(workflowDetailsUrl({ id: workflowId }));
        }
    }, [navigate, template, workflowId]);

    const saveWorkflowFormData = (formData: WorkflowEditFormData) => {
        if (workflow) {
            const formDataWithCurrentSteps: WorkflowEditFormData = {
                ...formData,
                steps: editableSteps ?? formData.steps,
            };
            setWorkflow(mergeFormDataIntoWorkflow(workflow, formDataWithCurrentSteps));
            setIsEditDialogOpen(false);
        }
    };

    const handleHeaderUpdate = useCallback(
        async (name: string, description: string) => {
            if (template && workflow) {
                setWorkflow({
                    ...workflow,
                    metadata: {
                        ...workflow.metadata,
                        name,
                        description,
                    },
                });
            } else if (workflowId) {
                try {
                    await updateWorkflow({
                        workflowId,
                        name,
                        description,
                    });
                } catch (error) {
                    toast({
                        title: "Failed to update automation header",
                        description: formatError(error),
                    });
                    throw error;
                }
            }
        },
        [template, workflow, workflowId, updateWorkflow, toast],
    );

    return (
        <div data-testid="workflow-edit-page" className="flex size-full flex-col">
            <div className="flex flex-col p-[26px]">
                {workflow ? (
                    <div className="flex flex-col gap-2">
                        <div className="flex items-start justify-between gap-8">
                            <AutomationEditableHeader
                                workflow={workflow}
                                onUpdate={handleHeaderUpdate}
                                onEditProperties={() => setIsEditDialogOpen(true)}
                            />
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" onClick={handleCancel} data-tracking-id="cancel">
                                        Cancel
                                    </Button>
                                    {template ? (
                                        <CreateAutomationButton
                                            workflow={workflow}
                                            editableStepsAtom={atoms.editableStepsAtom}
                                        />
                                    ) : (
                                        <SaveAutomationStepsButton
                                            workflowId={workflow.id}
                                            editableStepsAtom={atoms.editableStepsAtom}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <BreadcrumbsSkeleton itemsCount={2} />
                )}
                <ErrorMessage error={workflowError} />
            </div>

            {workflow && (
                <AutomationDefinitionSectionEdit
                    editableStepsAtom={atoms.editableStepsAtom}
                    stepIndexBeingEditedAtom={atoms.stepIndexBeingEditedAtom}
                    newStepAtom={atoms.newStepAtom}
                    draftStepAtom={atoms.draftStepAtom}
                    isCreatingStepAtom={atoms.isCreatingStepAtom}
                    removeStepAtom={atoms.removeStepAtom}
                    createNewStepAtom={atoms.createNewStepAtom}
                    createStepIfEmptyAtomEffect={atoms.createStepIfEmptyAtomEffect}
                    createDraftAtomEffect={atoms.createDraftAtomEffect}
                    changeDraftStepTypeAtom={atoms.changeDraftStepTypeAtom}
                    upsertDraftStepIntoStepsAtom={atoms.upsertDraftStepIntoStepsAtom}
                />
            )}

            <AutomationEditDialog
                open={isEditDialogOpen}
                onOpenChange={setIsEditDialogOpen}
                workflow={workflow}
                onSubmit={template ? saveWorkflowFormData : undefined}
            />
        </div>
    );
};
